import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {EmergencyContact} from "../../models/emergencyContact";
import {deleteEmergencyContact, getEmergencyContactsByAccount} from "../../api/emergencyContactApi";
import EmergencyContactDialog from "../dialogs/EmergencyContactDialog";
import EmergencyContactCard from "../cards/EmergencyContactCard";
import MessageBox, {MessageType} from "../components/MessageBox";
import {useRequiredAccount} from "../../hooks/AccountContext";

interface EmergencyContactsProps {

}

function EmergencyContacts() {
    const {t} = useTranslation();
    const [contacts, setContacts] = useState<EmergencyContact[]>([]);
    const [message, setMessage] = useState<MessageType>(null);
    const [disableAdding, setDisableAdding] = useState<boolean>(false);
    const [dialogMode, setDialogMode] = useState<"edit" | "create" | null>(null);
    const [editId, setEditId] = useState<number | null>(null);

    const {
        account
    } = useRequiredAccount();

    useEffect(() => {
        async function fetchContacts() {
            try {
                const data = await getEmergencyContactsByAccount(account.id);
                setContacts(data);
                setDisableAdding(false);
            } catch (err) {
                setDisableAdding(true);
                setMessage({type: "error", text: t("common.messages.loadError")});
            }
        }

        if (account?.id) {
            fetchContacts();
        }
    }, [account.id, t]);

    useEffect(() => {
        if (contacts.length === 0) return;

        const cards = document.querySelectorAll<HTMLElement>(".contact-card");
        if (!cards.length) return;

        cards.forEach(card => (card.style.height = "auto"));

        const maxHeight = Math.max(...Array.from(cards).map(c => c.offsetHeight));

        cards.forEach(card => (card.style.height = `${maxHeight}px`));
    }, [contacts]);

    const handleEdit = (id: number) => {
        setEditId(id);
        setDialogMode("edit");
    };

    const handleDelete = async (id: number) => {
        await deleteEmergencyContact(id);
        await reloadContacts();
    };

    const handleCreate = () => {
        setEditId(null);
        setDialogMode("create");
    };

    const closeDialog = () => {
        setEditId(null);
        setDialogMode(null);
    };

    async function reloadContacts() {
        try {
            const data = await getEmergencyContactsByAccount(account.id);
            setContacts(data);
        } catch {
            setMessage({type: "error", text: t("common.messages.loadError")});
        }
    }

    return (
        <div>
            <div>
                <h2>{t("menu.emergency")}</h2>
            </div>

            <div className="contact-section">
                <div className="contact-button-wrapper">
                    <button
                        type="button"
                        className="form-button"
                        disabled={disableAdding}
                        onClick={handleCreate}
                    >
                        {t("emergency.dialog.create")}
                    </button>
                </div>

                <MessageBox message={message}/>

                <div className="contact-grid scrollbar-area">
                    {contacts
                        .slice()
                        .sort((a, b) => {
                            const fromDiff = a.lastName.localeCompare(b.lastName);
                            if (fromDiff !== 0) return fromDiff;

                            return a.firstName.localeCompare(b.firstName);
                        })
                        .map((contact) => (
                            <EmergencyContactCard
                                emergencyContact={contact}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        ))}
                </div>
            </div>

            {dialogMode && (
                <EmergencyContactDialog
                    accountId={account.id}
                    emergencyContact={
                        dialogMode === "edit"
                            ? contacts.find((c) => c.id === editId) ?? null
                            : null
                    }
                    onClose={closeDialog}
                    onSaved={reloadContacts}
                />
            )}
        </div>
    );
}

export default EmergencyContacts;